"use client";

import { AlertTriangle, Clock, RefreshCw, Wifi, WifiOff } from "lucide-react";
import { useEffect, useState } from "react";

interface NetworkState {
  onLine: boolean;
  rtt?: number;
  downlink?: number;
  effectiveType?: string;
}

interface PendingRequest {
  id: string;
  action: string;
  elapsedMs: number;
  context?: {
    practiceId?: string;
    orgId?: number;
    appointmentId?: string;
  };
}

interface NetworkDiagnostics {
  getPendingRequests: () => PendingRequest[];
  getNetworkState: () => NetworkState;
  getSessionId: () => string | undefined;
  getSessionAgeMs: () => number | null;
  getMainThreadBlockingMs: () => Promise<number>;
  getStorageAccumulation: () => Record<string, number>;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

export function NetworkTab() {
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [networkState, setNetworkState] = useState<NetworkState | null>(null);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [sessionAgeMs, setSessionAgeMs] = useState<number | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [mainThreadBlockingMs, setMainThreadBlockingMs] = useState<
    number | null
  >(null);
  const [storageAccumulation, setStorageAccumulation] = useState<Record<
    string,
    number
  > | null>(null);

  // Stress test state
  const [stressTestState, setStressTestState] = useState<
    "idle" | "pending" | "blocking" | "done"
  >("idle");
  const [blockDurationMs, setBlockDurationMs] = useState("300");
  const [delayBeforeBlockMs, setDelayBeforeBlockMs] = useState("500");

  const refresh = async () => {
    const diag = (
      window as unknown as { __networkDiagnostics?: NetworkDiagnostics }
    ).__networkDiagnostics;

    if (diag) {
      setPendingRequests(diag.getPendingRequests());
      setNetworkState(diag.getNetworkState());
      setSessionId(diag.getSessionId());
      setSessionAgeMs(diag.getSessionAgeMs());
      setStorageAccumulation(diag.getStorageAccumulation());

      // Measure main thread blocking (non-blocking measurement)
      try {
        const blockingMs = await diag.getMainThreadBlockingMs();
        setMainThreadBlockingMs(blockingMs);
      } catch {
        // Silently fail if not available
      }
    }
    setLastRefresh(new Date());
  };

  // Auto-refresh every second
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    void refresh();
    const interval = setInterval(() => {
      void refresh();
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const hasStalRequests = pendingRequests.some((r) => r.elapsedMs > 10000);

  const handleBlockMainThread = () => {
    setStressTestState("pending");

    setTimeout(() => {
      setStressTestState("blocking");
      const startTime = performance.now();
      const duration = Number.parseFloat(blockDurationMs);

      // Busy loop to block the main thread
      while (performance.now() - startTime < duration) {
        // Spin - prevent any callbacks from firing
      }

      setStressTestState("done");
    }, Number.parseFloat(delayBeforeBlockMs));
  };

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">
            Network Diagnostics
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Monitor network requests and connectivity
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          title="Refresh"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Network Status */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          {networkState?.onLine ? (
            <Wifi className="h-4 w-4 text-green-500" />
          ) : (
            <WifiOff className="h-4 w-4 text-red-500" />
          )}
          Network Status
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Status:</span>{" "}
            <span
              className={
                networkState?.onLine ? "text-green-600" : "text-red-600"
              }
            >
              {networkState?.onLine ? "Online" : "Offline"}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Type:</span>{" "}
            <span className="text-gray-900">
              {networkState?.effectiveType || "Unknown"}
            </span>
          </div>
          <div>
            <span className="text-gray-500">RTT:</span>{" "}
            <span className="text-gray-900">
              {networkState?.rtt ? `${networkState.rtt}ms` : "N/A"}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Downlink:</span>{" "}
            <span className="text-gray-900">
              {networkState?.downlink ? `${networkState.downlink} Mbps` : "N/A"}
            </span>
          </div>
        </div>
      </div>

      {/* Session Info */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Clock className="h-4 w-4 text-blue-500" />
          Session Info
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Session ID:</span>{" "}
            <span className="text-gray-900 font-mono text-xs">
              {sessionId || "N/A"}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Session Age:</span>{" "}
            <span className="text-gray-900">
              {sessionAgeMs !== null ? formatDuration(sessionAgeMs) : "N/A"}
            </span>
          </div>
        </div>
      </div>

      {/* Main Thread & Storage */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          Resource Pressure
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <span className="text-gray-500">Main Thread Delay:</span>{" "}
            <span
              className={
                mainThreadBlockingMs !== null && mainThreadBlockingMs > 50
                  ? "text-orange-600 font-bold"
                  : "text-gray-900"
              }
            >
              {mainThreadBlockingMs !== null
                ? `${mainThreadBlockingMs.toFixed(0)}ms`
                : "Measuring..."}
            </span>
            {mainThreadBlockingMs !== null && mainThreadBlockingMs > 50 && (
              <span className="text-xs text-orange-600 ml-2">⚠️ High</span>
            )}
          </div>

          {storageAccumulation && (
            <div className="space-y-2 mt-3 pt-3 border-t border-gray-200">
              <div className="text-xs font-medium text-gray-600">
                Storage Accumulation:
              </div>
              {Object.entries(storageAccumulation).map(([key, bytes]) => (
                <div key={key} className="flex justify-between text-xs">
                  <span className="text-gray-600 capitalize">
                    {key.replace(/_/g, " ")}:
                  </span>
                  <span
                    className={
                      bytes > 1024 * 1024 ? "text-orange-600 font-bold" : ""
                    }
                  >
                    {bytes > 0 ? `${(bytes / 1024).toFixed(1)}KB` : "Empty"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pending Requests */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
            {hasStalRequests ? (
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            ) : (
              <div className="h-4 w-4 rounded-full bg-green-500" />
            )}
            Pending Requests
            {pendingRequests.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-gray-200 rounded-full text-xs">
                {pendingRequests.length}
              </span>
            )}
          </div>
          <span className="text-xs text-gray-400">
            Updated: {lastRefresh.toLocaleTimeString()}
          </span>
        </div>

        {pendingRequests.length === 0 ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <div className="text-green-600 text-lg mb-1">
              No pending requests
            </div>
            <div className="text-xs text-green-700">
              All network requests have completed
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {pendingRequests.map((req) => {
              const isStale = req.elapsedMs > 10000;
              return (
                <div
                  key={req.id}
                  className={`rounded-lg p-3 border ${
                    isStale
                      ? "bg-amber-50 border-amber-200"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm text-gray-900">
                      {req.action}
                    </span>
                    <span
                      className={`text-sm font-mono ${
                        isStale ? "text-amber-600 font-bold" : "text-gray-500"
                      }`}
                    >
                      {formatDuration(req.elapsedMs)}
                      {isStale && " (STALE)"}
                    </span>
                  </div>
                  {req.context && (
                    <div className="mt-1 text-xs text-gray-500">
                      {req.context.practiceId && (
                        <span className="mr-3">
                          Practice: {req.context.practiceId}
                        </span>
                      )}
                      {req.context.orgId && (
                        <span className="mr-3">Org: {req.context.orgId}</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Main Thread Stress Test */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
        <div className="text-sm font-medium text-red-900">
          Main Thread Stress Test
        </div>
        <p className="text-xs text-red-800">
          Block the main thread while a network request is pending to reproduce
          the hang. Start a check-in right after clicking the button.
        </p>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="delay-input"
                className="text-xs text-red-900 block mb-1"
              >
                Delay before block (ms):
              </label>
              <input
                id="delay-input"
                type="number"
                value={delayBeforeBlockMs}
                onChange={(e) => setDelayBeforeBlockMs(e.target.value)}
                disabled={stressTestState !== "idle"}
                className="w-full px-2 py-1 text-xs border border-red-300 rounded bg-white disabled:bg-gray-100"
              />
            </div>

            <div>
              <label
                htmlFor="duration-input"
                className="text-xs text-red-900 block mb-1"
              >
                Block duration (ms):
              </label>
              <input
                id="duration-input"
                type="number"
                value={blockDurationMs}
                onChange={(e) => setBlockDurationMs(e.target.value)}
                disabled={stressTestState !== "idle"}
                className="w-full px-2 py-1 text-xs border border-red-300 rounded bg-white disabled:bg-gray-100"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={handleBlockMainThread}
            disabled={stressTestState !== "idle"}
            className={`w-full px-3 py-2 text-xs font-medium rounded transition-colors ${
              stressTestState === "idle"
                ? "bg-red-600 text-white hover:bg-red-700"
                : stressTestState === "pending"
                  ? "bg-yellow-600 text-white"
                  : stressTestState === "blocking"
                    ? "bg-orange-600 text-white"
                    : "bg-green-600 text-white"
            }`}
          >
            {stressTestState === "idle"
              ? "Start Block (then run check-in)"
              : stressTestState === "pending"
                ? `Blocking in ${Math.ceil(Number.parseFloat(delayBeforeBlockMs) / 100) / 10}s...`
                : stressTestState === "blocking"
                  ? "🔴 BLOCKING NOW"
                  : "✓ Block complete"}
          </button>

          {stressTestState === "pending" && (
            <div className="text-xs text-yellow-700 bg-yellow-100 p-2 rounded border border-yellow-200">
              ⏱️ Preparing to block... Start your check-in flow soon!
            </div>
          )}

          {stressTestState === "blocking" && (
            <div className="text-xs text-red-700 bg-red-100 p-2 rounded border border-red-200 font-medium animate-pulse">
              ⚠️ Main thread is blocked NOW - quickly start a check-in!
            </div>
          )}

          {stressTestState === "done" && (
            <div className="text-xs text-green-700 bg-green-100 p-2 rounded border border-green-200">
              ✓ Block complete. Did the hang reproduce?
            </div>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="text-xs text-gray-400 border-t pt-4 space-y-2">
        <p>
          <strong>Stale requests</strong> ({">"} 10s) may indicate network
          issues. Check Datadog for{" "}
          <code className="bg-gray-100 px-1 rounded">
            Kiosk Network request stale
          </code>{" "}
          logs.
        </p>
        <p>
          <strong>Main Thread Delay</strong> ({">"} 50ms) indicates the browser
          is busy processing JavaScript. This can block promise callbacks.
        </p>
        <p>
          <strong>Mixpanel Events</strong> storage grows with each action. If
          Mixpanel batch isn't cleared between flows, it accumulates over time
          and can slow down localStorage operations.
        </p>
      </div>
    </div>
  );
}
